import { Box, Stack, styled, Typography } from "@mui/material";
import { PickersArrowSwitcher } from "@mui/x-date-pickers/internals";
import { Dayjs } from "dayjs";
import { createRef, useMemo, useRef } from "react";
import { CSSTransition, TransitionGroup } from "react-transition-group";

import { getMonthMatrix } from "../../../utils/helpers/date";

import { MonthRow } from "./MonthRow";

const StyledYearSwitcher = styled(PickersArrowSwitcher)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(2, 2, 0.5),
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

const TransitionWrapper = styled(Box)({
  position: "relative",
  width: "100%",
  height: "100%",
  "& .left-enter": {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    transform: "translateX(100%)",
    zIndex: 2,
  },
  "& .left-enter-active": {
    transform: "translateX(0)",
    transition: "transform 300ms ease-in-out",
    zIndex: 2,
  },
  "& .left-exit": {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    transform: "translateX(0)",
    zIndex: 1,
  },
  "& .left-exit-active": {
    transform: "translateX(-100%)",
    transition: "transform 300ms ease-in-out",
    zIndex: 1,
  },
  "& .right-enter": {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    transform: "translateX(-100%)",
    zIndex: 2,
  },
  "& .right-enter-active": {
    transform: "translateX(0)",
    transition: "transform 300ms ease-in-out",
    zIndex: 2,
  },
  "& .right-exit": {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    transform: "translateX(0)",
    zIndex: 1,
  },
  "& .right-exit-active": {
    transform: "translateX(100%)",
    transition: "transform 300ms ease-in-out",
    zIndex: 1,
  },
});

interface MonthGridProps {
  year: number;
  direction?: "left" | "right";
  minDate?: Dayjs;
  maxDate?: Dayjs;
  value?: [Dayjs | null, Dayjs | null];
  isNextHidden?: boolean;
  isPreviousHidden?: boolean;
  hoveredMonth: Dayjs | null;
  onHoverMonth?: (month: Dayjs) => void;
  onLeaveMonth?: () => void;
  onNextClick: () => void;
  onPreviousClick: () => void;
  onAnimationEnd: (animating: false) => void;
  onSelectMonth?: (month: Dayjs) => void;
}

export const MonthGrid = ({
  value,
  year,
  direction,
  minDate,
  maxDate,
  hoveredMonth,
  isNextHidden,
  isPreviousHidden,
  onNextClick,
  onPreviousClick,
  onAnimationEnd,
  onSelectMonth,
  onHoverMonth,
  onLeaveMonth,
}: MonthGridProps) => {
  const nodeRefs = useRef<{ [key: number]: React.RefObject<HTMLDivElement> }>(
    {},
  );
  if (!nodeRefs.current[year]) {
    nodeRefs.current[year] = createRef<HTMLDivElement>();
  }

  const isPrevDisabled = useMemo(
    () => (minDate ? year <= minDate.year() : false),
    [minDate, year],
  );
  const isNextDisabled = useMemo(
    () => (maxDate ? year >= maxDate.year() : false),
    [maxDate, year],
  );
  const months = useMemo(() => getMonthMatrix(year, 4), [year]);

  return (
    <Stack spacing={2} sx={{ width: 312, position: "relative" }}>
      <StyledYearSwitcher
        isNextDisabled={isNextDisabled}
        isNextHidden={isNextHidden}
        isPreviousDisabled={isPrevDisabled}
        isPreviousHidden={isPreviousHidden}
        nextLabel="Next year"
        previousLabel="Previous year"
        onGoToNext={onNextClick}
        onGoToPrevious={onPreviousClick}>
        <Typography variant="body1">{year}</Typography>
      </StyledYearSwitcher>

      <TransitionGroup component={TransitionWrapper}>
        <CSSTransition
          key={year}
          classNames={direction}
          nodeRef={nodeRefs.current[year]}
          timeout={300}
          onExited={() => onAnimationEnd(false)}>
          <Stack
            ref={nodeRefs.current[year]}
            spacing={0}
            sx={{ flex: 1, position: "relative" }}>
            {months.map((row, index) => (
              <MonthRow
                key={index}
                hoveredMonth={hoveredMonth}
                maxDate={maxDate}
                minDate={minDate}
                months={row}
                value={value}
                onHoverMonth={onHoverMonth}
                onLeaveMonth={onLeaveMonth}
                onSelect={onSelectMonth}
              />
            ))}
          </Stack>
        </CSSTransition>
      </TransitionGroup>
    </Stack>
  );
};
